import { Shape } from '@/services/shape';
import { ProgressBarAttribute } from '@/services/shape/attributes/progress-bar';
import {
  addResourceIfNotExists,
  generateLayoutAst,
  insertImages
} from '../utils/resource.utils';
import { isSourceKeyFormat } from '@/utils/number';
import { getRefName } from '../utils/name.utils';
import {
  getRealSourceKey,
  getSourceValueArr
} from '../utils/data-source.utils';

export const getProgressBarAst = (resourceAst: Ast, shape: Shape) => {
  const attrs = shape.attrs as ProgressBarAttribute;
  const {
    resources,
    recolorEnable,
    endingStyle,
    progressBarType = 'arc',
    valueStart,
    valueEnd,
    format,
    pivotX,
    pivotY,
    barRadius,
    barWidth,
    alpha = 1,
  } = attrs;

  // 插入图片:背景图和进度条图片
  const imageMap = insertImages(resourceAst, resources, {
    recolorEnable,
    imgFormat: format,
    alpha
  });

  const layoutAsts: Ast[] = [];
  const mainSourceItem = shape.sourceItem;

  const valueStartArr = getSourceValueArr(valueStart, mainSourceItem);
  const valueEndArr = getSourceValueArr(valueEnd, mainSourceItem);

  const isMultiUnit = shape.sourceItems?.length > 1;
  shape.sourceItems.forEach((sourceItem, index) => {
    const { key: sourceKey = '' } = sourceItem ?? {};
    const valueStart = valueStartArr[index];
    const valueEnd = valueEndArr[index];
    // 添加上图片数字或图片数值对
    const tag =
      progressBarType == 'arc'
        ? 'DataItemArcProgressBar'
        : 'DataItemLineProgressBar';
    const progressBarAst: Ast = {
      tag,
      attrsMap: {
        source: getRealSourceKey(sourceKey),
        ref: getRefName(imageMap['progress']),
        bg: getRefName(imageMap['bg']),
        endingStyle,
        valueStart: String(valueStart),
        valueRange: isSourceKeyFormat(valueEnd)
          ? getRealSourceKey(String(valueEnd))
          : String(Number(valueEnd) - Number(valueStart)),
        indicatorImage: getRefName(imageMap['indicatorImage']),
        supportRecolor: String(recolorEnable ?? false),
        renderRule: 'alwaysShow'
      },
      children: []
    };
    if (isMultiUnit) {
      progressBarAst.attrsMap['renderRule'] = 'hideWhenUnitMismatch';
    }
    // 根据不同类型，添加不同属性
    if (attrs.progressBarType == 'arc') {
      const { w = 0, innerRingPercent = 0 } = attrs;
      const r = w / 2;
      const innerRingRadius = Math.floor((r * innerRingPercent) / 100);
      const _barWidth =
        barWidth != undefined ? Number(barWidth) : r - innerRingRadius;
      const _barRadius =
        barRadius != undefined
          ? Number(barRadius)
          : innerRingRadius + _barWidth / 2;

      Object.assign(progressBarAst.attrsMap, {
        angleStart: attrs.angleStart,
        angleRange: attrs.angleEnd - attrs.angleStart,
        pivotX: pivotX != undefined ? pivotX : attrs.w / 2,
        pivotY: pivotY != undefined ? pivotY : attrs.h / 2,
        barWidth: _barWidth,
        // 弧形进度条为百分值
        barRadius: _barRadius,
        // 如果indicatorRadius没有设置，默认设置为0（和导出bin时，保持一致）
        indicatorRadius: attrs.indicatorRadius ? attrs.indicatorRadius : '0'
      });
    }
    if (attrs.progressBarType == 'line') {
      const dirConfig: {
        [key in Exclude<
          ProgressBarAttribute['growDirection'],
          'negative' | 'positive'
        >]: {
          startX: number;
          startY: number;
          endX: number;
          endY: number;
        };
      } = {
        'to-right': {
          startX: 0,
          startY: attrs.h / 2,
          endX: attrs.w,
          endY: attrs.h / 2
        },
        'to-left': {
          startX: attrs.w,
          startY: attrs.h / 2,
          endX: 0,
          endY: attrs.h / 2
        },
        'to-down': {
          startX: attrs.w / 2,
          startY: 0,
          endX: attrs.w / 2,
          endY: attrs.h
        },
        'to-up': {
          startX: attrs.w / 2,
          startY: attrs.h,
          endX: attrs.w / 2,
          endY: 0
        }
      };
      const dir = dirConfig[attrs.growDirection as 'to-right'];
      Object.assign(progressBarAst.attrsMap, {
        startX: dir?.startX ?? 0,
        startY: dir?.startY ?? 0,
        endX: dir?.endX ?? 0,
        endY: dir?.endY ?? 0,
        barWidth:
          attrs.growDirection == 'to-left' || attrs.growDirection == 'to-right'
            ? attrs.h
            : attrs.w
      });
    }

    const ast = addResourceIfNotExists(resourceAst, progressBarAst);

    // 生成 layout ast
    const layoutAst: Ast = generateLayoutAst({
      ref: '@' + ast.attrsMap.name,
      x: attrs.x,
      y: attrs.y
    });
    layoutAsts.push(layoutAst);
  });

  return layoutAsts;
};
